<template>
  <div class="station-form-category-container">
    <div class="station-form-category-content">
      <!-- QA-09: empilhado (celular) usa o gap-2 das linhas do médico; o desktop segue gap-4. -->
      <div class="flex" :class="stacked ? 'flex-col gap-2' : 'flex-wrap gap-4'">
        <v-autocomplete
          @update:model-value="setCompany"
          :items="listCompanies"
          :model-value="company ? String(company) : undefined"
          class="flex-1 min-w-0 md:min-w-[200px]"
          color="primary20"
          label="Empresa"
          theme="light"
          variant="outlined"
          density="comfortable"
          :error-messages="companyErr"
        />

        <v-autocomplete
          @update:model-value="setHealthUnit"
          :items="listHealthUnitsByCompany"
          :model-value="healthUnit ? String(healthUnit) : undefined"
          :disabled="!company"
          :loading="isLoadingUnits"
          class="flex-1 min-w-0 md:min-w-[200px]"
          color="primary20"
          label="Unidade"
          variant="outlined"
          density="comfortable"
          :error-messages="healthUnitErr"
        />

        <v-autocomplete
          @update:model-value="setSchedule"
          :items="listSchedules"
          :model-value="schedule ? String(schedule) : undefined"
          :disabled="!healthUnit"
          :loading="isLoadingSchedules"
          class="flex-1 min-w-0 md:min-w-[200px]"
          color="primary20"
          label="Escala"
          theme="light"
          variant="outlined"
          density="comfortable"
          :error-messages="scheduleErr"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { usePositionStore } from '@/pinia-store/usePositionStore'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useErrorToast } from '@/composables/useErrorToast'

// `stacked`: uma coluna com largura cheia (Criar plantão em tela cheia no celular, R6).
defineProps([
  'company',
  'healthUnit',
  'schedule',
  'companyErr',
  'healthUnitErr',
  'scheduleErr',
  'doctor',
  'stacked'
])
/* eslint-disable sonarjs/no-duplicate-string */
const emit = defineEmits([
  'update:company',
  'update:healthUnit',
  'update:schedule',
  'update:doctor',
  'update:requiresDoctorPaymentFormation'
])

const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()
const doctorStore = useDoctorStore()
const { listCompanies } = storeToRefs(useCompanyStore())
const { listHealthUnitsByCompany } = storeToRefs(useHealthUnitStore())
const { listSchedules } = storeToRefs(useScheduleStore())

const positionStore = usePositionStore()

const { notifyErrorMessage } = useErrorToast()
const DOCTORS_LOAD_ERROR = 'Não foi possível carregar a lista de médicos'

const isLoadingUnits = ref(false)
const isLoadingSchedules = ref(false)

const setCompany = async (v: string) => {
  isLoadingUnits.value = true

  const companyId = Number(v)
  emit('update:company', Number(companyId))
  emit('update:healthUnit', undefined)
  emit('update:schedule', undefined)
  emit('update:doctor', undefined)

  try {
    await healthUnitStore.setHealthUnitsByCompany(companyId)

    await doctorStore.applyFilters(
      {
        search: '',
        names: [],
        crms: [],
        crmUFs: [],
        companies: [String(companyId)],
        inviteStatus: [
          CompanyDoctorInviteStatusEnum.ACCEPTED,
          CompanyDoctorInviteStatusEnum.PENDING
        ]
      },
      false
    )
    // `fetchDoctors` não relança: a falha da lista de médicos só aparece em `isError`.
    if (doctorStore.isError) notifyErrorMessage(DOCTORS_LOAD_ERROR)
  } catch (error) {
    // fallback intencional: as duas stores tratam a falha da API sem relançar (unidades
    // com toast próprio; médicos pelo `isError` acima), então só erro de código chega aqui.
    console.error('Erro ao carregar unidades ou médicos:', error)
  } finally {
    isLoadingUnits.value = false
  }
}

const setHealthUnit = async (v: string) => {
  isLoadingSchedules.value = true

  const healthUnitId = Number(v)
  emit('update:healthUnit', healthUnitId)
  emit('update:schedule', undefined)

  try {
    await scheduleStore.setSchedulesByHealthUnit(healthUnitId)
    // `getAllSchedules` não relança: guarda a mensagem já normalizada (`getApiErrorMessage`)
    // em `allSchedulesLoadError` e deixa a lista vazia — sem isto o seletor de escala
    // ficava vazio sem aviso.
    if (scheduleStore.allSchedulesLoadError) {
      notifyErrorMessage(scheduleStore.allSchedulesLoadError)
    }
  } catch (error) {
    // fallback intencional: a store não relança a falha da API (ver acima), então só
    // erro de código chega aqui.
    console.error('Erro ao carregar escalas:', error)
  } finally {
    isLoadingSchedules.value = false
  }
}

const setSchedule = async (v: string) => {
  const scheduleId = Number(v)
  const selectedSchedule = listSchedules.value.find((s) => s.value === v)

  await positionStore.getByScheduleId(scheduleId)
  emit('update:schedule', Number(scheduleId))

  if (selectedSchedule) {
    emit('update:requiresDoctorPaymentFormation', selectedSchedule.requiresDoctorPaymentFormation)
  }
}
</script>
